import React, { useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  Combobox,
  Input,
  Radio,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { RequestError } from 'components/Errors/RequestError'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { MovePayload } from 'pages/NestedCustomers/types'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { SourceWalletTypes } from 'types/move/move'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { apiErrorsAtom } from './atoms/atoms'
import { useMoveFormConditions } from './hooks/useMoveFormConditions'
import { useMoveFormHandlers } from './hooks/useMoveFormHandlers'
import { useMoveFormOptions } from './hooks/useMoveFormOptions'
import { useMoveFormQueryData } from './hooks/useMoveFormQueryData'
import { useMoveFormState } from './hooks/useMoveFormState'
import { useMoveFormValidation } from './hooks/useMoveFormValidation'
import { useSourceWallet } from './hooks/useSourceWallet'
import { getFieldsToValidate } from './utils'

interface MoveFormProps {
  onContinue: (formData: MovePayload) => void
  onCancel: () => void
  isSubmitting?: boolean
}

export const MoveForm = ({ onContinue, onCancel, isSubmitting = false }: MoveFormProps) => {
  const { t } = useTranslation()
  const [walletAction, setWalletAction] = useWalletActionContext()
  const apiErrors = useAtomValue(apiErrorsAtom)
  const sourceWallet = useSourceWallet()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const form = useMoveFormState()

  const {
    customerList,
    transferWallets,
    isFetchingCustomers,
    isFetchingTransferWallets,
    setSearchQuery,
    queryFetchingErrors,
    fetchTransferWallets,
  } = useMoveFormQueryData({
    sourceWallet,
    formValues: form.values,
  })

  const validation = useMoveFormValidation({
    sourceWallet,
    formValues: form.values,
    clearErrors: form.clearErrors,
    setError: form.setError,
  })

  const formConditions = useMoveFormConditions({
    sourceWallet,
    customerModel: walletAction?.meta?.customerModel,
    hasTransferWallets: !!transferWallets?.length,
    isLoading: isFetchingCustomers || isFetchingTransferWallets,
    formValues: form.values,
    isInsufficientBalance: validation.isInsufficientBalance,
    isExceedingMaxAmount: validation.isExceedingMaxAmount,
  })

  const {
    customerOptions,
    transferWalletOptions,
    selectBeneficiaryDropdownPlaceholder,
    destinationWalletLabel,
    selectedSourceWalletOption,
    walletTransferTypeOptions,
  } = useMoveFormOptions({
    customerList,
    transferWallets,
    sourceWallet,
    isEmbeddedOrRelianceCustomerWallet:
      formConditions.isEmbeddedOrRelianceCustomerModel &&
      sourceWallet?.walletType === SourceWalletTypes.CUSTOMER,
    customerHasNoModel: formConditions.customerHasNoModel,
    isCustomerWalletTransferType: formConditions.isCustomerWalletTransferType,
  })

  const {
    handleWalletTransferTypeSelection,
    handleCustomerSelection,
    handleDestinationWalletSelection,
    handleSearchQueryChange,
    handleAmountChange,
    handleAmountBlur,
    handlePaymentReferenceChange,
  } = useMoveFormHandlers({
    setValue: form.setValue,
    validateAmount: validation.validateAmount,
    customerOptions,
    transferWalletOptions,
    setSearchQuery,
  })

  const handleContinue = async () => {
    const fieldsToValidate = getFieldsToValidate(form.values?.walletTransferType)
    const isValidDetails = await form.trigger(fieldsToValidate)

    if (isValidDetails) {
      setWalletAction({
        ...walletAction,
        header: t('customers.reviewTransaction'),
      })
      onContinue(form.values)
    }
  }

  useEffect(() => {
    if (apiErrors?.length) {
      apiErrors.forEach(err => {
        form.setError(err.parameter as keyof MovePayload, {
          message: err.message,
        })
      })
    }
  }, [apiErrors, form.setError])

  useEffect(() => {
    const shouldFetch =
      form.values?.walletTransferType === WalletTransferType.MAIN ||
      (form.values?.walletTransferType === WalletTransferType.CUSTOMER &&
        form.values?.customerReference)

    if (shouldFetch) {
      fetchTransferWallets()
    }
  }, [
    form.values?.walletTransferType,
    form.values?.customerReference,
    fetchTransferWallets,
    form.trigger,
  ])

  useEffect(() => {
    const error = (queryFetchingErrors.transferWallets ||
      queryFetchingErrors.customers) as BaseErrorResponse

    if (error) {
      setRequestError({
        errorCode: error.status,
        show: true,
      })
    } else {
      resetRequestError()
    }
  }, [queryFetchingErrors.customers, queryFetchingErrors.transferWallets])

  return (
    <Box paddingX={24} width="full" className="my-6 space-y-4 text-gray-100">
      {requestError?.show && <RequestError sentryTraceId={requestError?.sentryTraceId} />}

      <Combobox
        label={t('customers.moveFromWallet')}
        disabled={true}
        defaultValue={sourceWallet?.walletId}
        selectPlaceholder={sourceWallet ? undefined : t('noWallets')}
        id="sourceWallet"
        items={selectedSourceWalletOption}
        loadingInline={isFetchingTransferWallets}
      />
      {validation.isInsufficientBalance ? (
        <Callout variant="error">
          {t('payInsufficient', { currencyCode: sourceWallet?.currency })}
        </Callout>
      ) : (
        <>
          {formConditions.isWalletTransferTypeEnabled && (
            <Box>
              <Radio
                {...form.register('walletTransferType')}
                name="walletTransferType"
                horizontal={true}
                onChange={handleWalletTransferTypeSelection}
                options={walletTransferTypeOptions}
                value={form.values?.walletTransferType}
                error={form.state.errors?.walletTransferType?.message}
              />
            </Box>
          )}

          {formConditions.isCustomerWalletTransferType && (
            <Combobox
              {...form.register('customerReference')}
              label={t('customers.selectCustomer')}
              defaultValue={form.values?.customerReference}
              selectPlaceholder={t('select')}
              id="customerReference"
              items={customerOptions}
              onChange={handleCustomerSelection}
              loadingInline={isFetchingCustomers}
              disabled={isFetchingCustomers}
              errorText={form.state.errors?.customerReference?.message}
              onInputChange={handleSearchQueryChange}
              loading={isFetchingCustomers}
            />
          )}

          {isFetchingTransferWallets && (
            <Box paddingT={24}>
              <RowsSkeletonLoader rows={6} />
            </Box>
          )}

          {!isFetchingTransferWallets && formConditions.showDestinationWalletCombobox && (
            <Combobox
              {...form.register('destinationWallet')}
              key={form.values?.walletTransferType}
              label={destinationWalletLabel}
              selectPlaceholder={selectBeneficiaryDropdownPlaceholder}
              id="destinationWallet"
              items={transferWalletOptions}
              onChange={handleDestinationWalletSelection}
              loadingInline={isFetchingTransferWallets}
              errorText={form.state.errors?.destinationWallet?.message}
              disabled={
                formConditions.isCustomerWalletTransferType
                  ? !form.values?.customerReference || isFetchingTransferWallets
                  : isFetchingTransferWallets
              }
              defaultValue={form.values?.destinationWallet}
            />
          )}

          {formConditions.showNoWalletsCallout && (
            <Callout variant="warning">{t('noWallets')}</Callout>
          )}

          {formConditions.showContent && (
            <>
              <Input
                testId="update-amount-input"
                {...form.register('amount')}
                onChange={handleAmountChange}
                label={t('massPayouts.amount')}
                inputMode="text"
                rightSuffixText={sourceWallet?.currency}
                name="amount"
                onBlur={handleAmountBlur}
                value={form.values?.amount}
                error={!!form.state.errors?.amount}
                errorText={form.state.errors?.amount?.message}
                placeholder="0.00"
              />

              <Input
                testId="update-reference-input"
                {...form.register('paymentReference')}
                onChange={handlePaymentReferenceChange}
                label={t('massPayouts.paymentReference')}
                placeholder={t('inputPlaceholders.beneficiaryPaymentReferencePlaceHolder')}
                inputMode="text"
                name="paymentReference"
                value={form.values?.paymentReference}
                error={!!form.state.errors?.paymentReference}
                errorText={form.state.errors?.paymentReference?.message}
              />
            </>
          )}
          <Box className="flex justify-between pt-2">
            <Button variant="secondary" onClick={onCancel}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleContinue}
              disabled={formConditions.isDisabled || apiErrors?.length > 0 || isSubmitting}
            >
              {t('continue')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}
